import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { GLOBAL } from '@cp/src/constants/Global';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { getDeviceId } from '@cp/src/utils/FingerPrintJS';
import store from '@cp/src/store';
import {
  setError,
  setFeedbackInteractionReferenceId,
  uploadComplete
} from '@cp/src/reducers/whatsappCallSlice';
import { createKey } from '@cp/src/utils/CaseDetail.utils';
import { ECallingType } from '../../../interfaces/index.type';
import { FeedbackPayload } from './interface';
import { Dispatch } from '@reduxjs/toolkit';

async function putBlobToPresignedUrl(url: string, blob: Blob): Promise<void> {
  const contentType = blob.type || 'audio/webm';
  const res = await fetch(url, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': contentType }
  });
  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status}`);
  }
}

interface UploadRecordingParams {
  blob: Blob;
  phoneNumber: string;
  loanId: string;
  customerId: string;
}

export const uploadRecording =
  ({ blob, phoneNumber, loanId, customerId }: UploadRecordingParams) =>
  async (dispatch: Dispatch) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_UploadStarted, {
      phone: phoneNumber
    });

    const user = store.getState()?.common?.userData;
    const telephonesData =
      store.getState()?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.telephonesv2;
    const selectedTelephone:
      | {
          number: string;
          source: string;
          numberCustomId: string;
        }
      | undefined = telephonesData?.data
      ?.map(telephone => ({
        number: telephone.number,
        source: telephone.source,
        numberCustomId: telephone.numberCustomId
      }))
      ?.find(telephone => telephone.number === phoneNumber);

    try {
      const presignedUrlEndpoint = getApiUrl(ApiKeys.GET_PRE_SIGNED_URL, {
        agentID: GLOBAL.AGENT_ID,
        deviceID: getDeviceId(),
        dataSyncType: 'CALL_RECORDING_AAC'
      });
      const presignedResponse = await axiosInstance.get(presignedUrlEndpoint);
      const preSignedUrl = presignedResponse?.data?.preSignedUrl;
      const requestId = presignedResponse?.data?.requestId;

      if (!preSignedUrl) {
        dispatch(setError('Failed to get upload URL.'));
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_UploadFailed, {
          reason: 'no_presigned_url',
          customerId
        });
        return;
      }

      // Blob is WebM/Opus from MediaRecorder (see mediaManager.ts), not AAC. Backend should use a
      // dataSyncType / S3 key that matches audio/webm, or transcode server-side if AAC is required.
      try {
        await putBlobToPresignedUrl(preSignedUrl, blob);
      } catch (uploadErr) {
        dispatch(setError('Failed to upload recording.'));
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_UploadFailed, {
          reason: 'upload_failed',
          customerId
        });
        return;
      }

      const ackUrl = getApiUrl(ApiKeys.SEND_UPLOAD_ACK, { requestId });
      const ackResponse = await axiosInstance.put(ackUrl, {
        syncSize: 0,
        syncStartOffset: 0,
        syncEndOffset: 0,
        metadata: {
          displayName: null,
          emails: [],
          phoneNumbers: [phoneNumber],
          timestamp: Date.now(),
          callType: 'WHATSAPP_CALL'
        }
      });

      if (ackResponse.status !== 200) {
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_UploadFailed, {
          reason: 'acknowledgement_failed',
          customerId
        });
        toast.error('Unable to upload the recording. Something went wrong.');
        return;
      }

      const path = ackResponse?.data?.relativePath;
      const bucketName = ackResponse?.data?.bucketName;
      const feedbackPayload: FeedbackPayload = {
        feedbacks: [],
        accountNumber: loanId,
        agentPhoneNumber: user?.phoneNumber || '',
        interactionReminderDTO: null,
        recipientName: user?.name || '',
        recipientPhoneNumber: phoneNumber || '',
        recipientPhoneNumberCustomId: selectedTelephone?.numberCustomId || '',
        sourceType: selectedTelephone?.source
          ? `${selectedTelephone.source}_PHONE_CALL`
          : 'NO_SOURCE_SELECTED',
        comments: '',
        interactionType: ECallingType.WHATSAPP_CALL,
        metadata: {
          bucketPath: bucketName,
          recordingPath: path,
          whatsappCallTimestamp: Date.now()
        }
      };

      toast.success('Recording uploaded successfully. Please fill the feedback now.');
      await createInteraction(loanId, customerId, feedbackPayload);

      dispatch(uploadComplete());
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_UploadSuccess, {
        phone: phoneNumber,
        customerId
      });
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_AwaitingFeedback, {
        phone: phoneNumber,
        customerId
      });
    } catch (error: any) {
      const errMsg =
        'Recording upload failed. Please download the recording and upload it manually.';
      dispatch(setError(errMsg));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_WhatsappCall_UploadFailed, {
        error: errMsg,
        customerId
      });
    }
  };

export const createInteraction = async (
  loanaccountnumber: string,
  customerreferenceid: string,
  body: FeedbackPayload,
  successCallback?: () => void,
  failureCallback?: () => void
): Promise<void> => {
  try {
    const response = await axiosInstance.post(getApiUrl(ApiKeys.SUBMIT_FEEDBACK), body, {
      headers: {
        customerreferenceid,
        loanaccountnumber,
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    });
    const referenceId = response?.data?.referenceId;
    if (referenceId) {
      store.dispatch(setFeedbackInteractionReferenceId(referenceId));
    }
    successCallback?.();
  } catch (_err: any) {
    toast(
      'Unable to update the call. Please fill the feedback and upload the recording after downloading',
      {
        type: 'error'
      }
    );
    failureCallback?.();
  }
};
